/**
 * VehicleTiresTab - Däck-flik i fordonskortet
 * Visar monterade och lagrade däck + byteshistorik med slitage och km.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { serviceApi, VehicleTire, TireChangeLogEntry } from '@/services/service/serviceApi';
import TireManagementModal from '@/components/service/TireManagementModal';

interface VehicleTiresTabProps {
  vehicleId: number;
}

const tireTypeLabels: Record<string, string> = {
  SUMMER: 'Sommardäck',
  WINTER_FRICTION: 'Snödäck (friktion)',
  WINTER_STUDDED: 'Dubbdäck',
};

const tireTypeIcon: Record<string, string> = {
  SUMMER: '☀️',
  WINTER_FRICTION: '❄️',
  WINTER_STUDDED: '❄️🔩',
};

function treadColor(mm: number | null): string {
  if (mm == null) return 'text-textTertiary';
  if (mm < 3) return 'text-red-600 font-semibold';
  if (mm < 4) return 'text-amber-600';
  return 'text-green-700';
}

function wornColor(mm: number | null): string {
  if (mm == null) return 'text-textTertiary';
  if (mm > 4) return 'text-red-600 font-semibold';
  if (mm > 2) return 'text-amber-600';
  return 'text-green-700';
}

function TreadDepthGrid({ tire }: { tire: VehicleTire }) {
  const hasPerWheel = tire.treadDepthVfMm != null || tire.treadDepthHfMm != null
    || tire.treadDepthVbMm != null || tire.treadDepthHbMm != null;
  if (!hasPerWheel) {
    if (tire.treadDepthMm == null) return null;
    return (
      <span className={`text-xs ${treadColor(tire.treadDepthMm)}`}>
        Mönsterdjup: {tire.treadDepthMm} mm
      </span>
    );
  }
  const cells = [
    { label: 'VF', val: tire.treadDepthVfMm },
    { label: 'HF', val: tire.treadDepthHfMm },
    { label: 'VB', val: tire.treadDepthVbMm },
    { label: 'HB', val: tire.treadDepthHbMm },
  ];
  return (
    <div className="mt-1.5">
      <div className="text-xs text-textTertiary mb-1">Mönsterdjup per hjul</div>
      <div className="grid grid-cols-4 gap-1">
        {cells.map(({ label, val }) => (
          <div key={label} className="text-center bg-gray-50 rounded px-1 py-1">
            <div className="text-xs text-textTertiary">{label}</div>
            <div className={`text-sm font-medium ${treadColor(val)}`}>
              {val != null ? `${val}` : '–'}
            </div>
            {val != null && <div className="text-xs text-textTertiary">mm</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Byteshistorik-rad
// ============================================================

function ChangeLogRow({ entry }: { entry: TireChangeLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  const wornCells = [
    { label: 'VF', val: entry.treadWornVfMm },
    { label: 'HF', val: entry.treadWornHfMm },
    { label: 'VB', val: entry.treadWornVbMm },
    { label: 'HB', val: entry.treadWornHbMm },
  ];
  const hasWorn = wornCells.some(c => c.val != null);

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Huvudrad */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Datum */}
            <div className="text-sm font-medium text-gray-800 whitespace-nowrap">
              {entry.changeDate}
            </div>

            {/* Byte: avmonterat → monterat */}
            <div className="flex items-center gap-2 text-sm flex-wrap">
              {entry.removedTireType && (
                <span className="flex items-center gap-1 text-textTertiary">
                  <span>{tireTypeIcon[entry.removedTireType] || '🛞'}</span>
                  <span>{tireTypeLabels[entry.removedTireType] || entry.removedTireType}</span>
                  {entry.removedTreadMinMm != null && (
                    <span className={`text-xs ${treadColor(entry.removedTreadMinMm)}`}>
                      ({entry.removedTreadMinMm} mm)
                    </span>
                  )}
                </span>
              )}
              {entry.removedTireType && entry.installedTireType && (
                <span className="text-gray-300">→</span>
              )}
              {entry.installedTireType && (
                <span className="flex items-center gap-1 font-medium text-gray-700">
                  <span>{tireTypeIcon[entry.installedTireType] || '🛞'}</span>
                  <span>{tireTypeLabels[entry.installedTireType] || entry.installedTireType}</span>
                  {entry.installedTreadMinMm != null && (
                    <span className="text-xs text-green-600">({entry.installedTreadMinMm} mm)</span>
                  )}
                </span>
              )}
            </div>

            {/* Km + slitage snabb-info */}
            <div className="flex items-center gap-3 ml-auto text-xs text-textTertiary whitespace-nowrap">
              {entry.kmDrivenOnRemoved != null && (
                <span className="flex items-center gap-1">
                  🛣️ <span className="font-medium text-gray-700">{entry.kmDrivenOnRemoved.toLocaleString()} km</span>
                </span>
              )}
              {entry.treadWornAvgMm != null && (
                <span className="flex items-center gap-1">
                  📉 <span className={`font-medium ${wornColor(entry.treadWornAvgMm)}`}>{entry.treadWornAvgMm} mm slitage</span>
                </span>
              )}
            </div>
          </div>
          {expanded
            ? <ChevronUpIcon className="h-4 w-4 text-textTertiary flex-shrink-0" />
            : <ChevronDownIcon className="h-4 w-4 text-textTertiary flex-shrink-0" />}
        </div>
      </button>

      {/* Expanderad detaljvy */}
      {expanded && (
        <div className="border-t bg-gray-50 px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          {/* Avmonterat */}
          <div>
            <div className="text-xs font-semibold text-textTertiary uppercase mb-2">Avmonterat</div>
            {entry.removedTireType ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span>{tireTypeIcon[entry.removedTireType]}</span>
                  <span>{tireTypeLabels[entry.removedTireType]}</span>
                </div>
                {entry.removedTireBrand && <div className="text-textTertiary text-xs">{entry.removedTireBrand} {entry.removedTireSize}</div>}
                {entry.removedInstalledAt && <div className="text-textTertiary text-xs">Monterades: {entry.removedInstalledAt}</div>}
                <div className="grid grid-cols-4 gap-1 mt-2">
                  {[
                    { label: 'VF', val: entry.removedTreadVfMm },
                    { label: 'HF', val: entry.removedTreadHfMm },
                    { label: 'VB', val: entry.removedTreadVbMm },
                    { label: 'HB', val: entry.removedTreadHbMm },
                  ].map(({ label, val }) => (
                    <div key={label} className="text-center bg-white border rounded py-1 px-1">
                      <div className="text-xs text-textTertiary">{label}</div>
                      <div className={`text-xs font-medium ${treadColor(val)}`}>{val ?? '–'}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : <span className="text-textTertiary text-xs">–</span>}
          </div>

          {/* Monterat */}
          <div>
            <div className="text-xs font-semibold text-textTertiary uppercase mb-2">Monterat</div>
            {entry.installedTireType ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span>{tireTypeIcon[entry.installedTireType]}</span>
                  <span>{tireTypeLabels[entry.installedTireType]}</span>
                </div>
                {entry.installedTireBrand && <div className="text-textTertiary text-xs">{entry.installedTireBrand} {entry.installedTireSize}</div>}
                <div className="grid grid-cols-4 gap-1 mt-2">
                  {[
                    { label: 'VF', val: entry.installedTreadVfMm },
                    { label: 'HF', val: entry.installedTreadHfMm },
                    { label: 'VB', val: entry.installedTreadVbMm },
                    { label: 'HB', val: entry.installedTreadHbMm },
                  ].map(({ label, val }) => (
                    <div key={label} className="text-center bg-white border rounded py-1 px-1">
                      <div className="text-xs text-textTertiary">{label}</div>
                      <div className={`text-xs font-medium ${treadColor(val)}`}>{val ?? '–'}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : <span className="text-textTertiary text-xs">–</span>}
          </div>

          {/* Statistik */}
          <div>
            <div className="text-xs font-semibold text-textTertiary uppercase mb-2">Statistik</div>
            <div className="space-y-2">
              {entry.kmDrivenOnRemoved != null && (
                <div>
                  <div className="text-xs text-textTertiary">Körda km på avmonterat däck</div>
                  <div className="font-semibold text-gray-800">{entry.kmDrivenOnRemoved.toLocaleString()} km</div>
                </div>
              )}
              {entry.odometerAtChange != null && (
                <div>
                  <div className="text-xs text-textTertiary">Mätarställning vid byte</div>
                  <div className="text-gray-700">{entry.odometerAtChange.toLocaleString()} km</div>
                </div>
              )}
              {hasWorn && (
                <div>
                  <div className="text-xs text-textTertiary mb-1">Slitage per hjul (mm)</div>
                  <div className="grid grid-cols-4 gap-1">
                    {wornCells.map(({ label, val }) => (
                      <div key={label} className="text-center bg-white border rounded py-1 px-1">
                        <div className="text-xs text-textTertiary">{label}</div>
                        <div className={`text-xs font-medium ${wornColor(val)}`}>{val ?? '–'}</div>
                      </div>
                    ))}
                  </div>
                  {entry.treadWornAvgMm != null && (
                    <div className="mt-1 text-xs text-textTertiary">
                      Snitt: <span className={`font-medium ${wornColor(entry.treadWornAvgMm)}`}>{entry.treadWornAvgMm} mm</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Huvudkomponent
// ============================================================

const VehicleTiresTab: React.FC<VehicleTiresTabProps> = ({ vehicleId }) => {
  const { t } = useTranslation();
  const [tires, setTires] = useState<VehicleTire[]>([]);
  const [changeLog, setChangeLog] = useState<TireChangeLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTireModal, setShowTireModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tiresData, logData] = await Promise.all([
        serviceApi.getTires(vehicleId),
        serviceApi.getTireChangeLog(vehicleId),
      ]);
      setTires(tiresData);
      setChangeLog(logData);
    } catch (err) {
      console.error('Failed to load tire data:', err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  const installed = tires.filter(t => t.status === 'INSTALLED');
  const stored = tires.filter(t => t.status === 'STORED');
  const other = tires.filter(t => t.status !== 'INSTALLED' && t.status !== 'STORED');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <span className="text-lg">🛞</span>
          {t('service.tires.title') || 'Däck'}
          {tires.length > 0 && <span className="text-xs text-textTertiary font-normal">({tires.length} st)</span>}
        </h3>
        <button
          onClick={() => setShowTireModal(true)}
          className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50 text-textSecondary flex items-center gap-1"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {t('service.tires.manage') || 'Hantera däck'}
        </button>
      </div>

      {tires.length === 0 ? (
        <div className="text-center py-10 text-textTertiary text-sm border rounded-lg">
          <div className="text-3xl mb-2">🛞</div>
          {t('service.tires.none') || 'Inga däck registrerade'}
          <div className="mt-3">
            <button onClick={() => setShowTireModal(true)} className="text-primary hover:underline text-sm">
              {t('service.tires.add') || 'Lägg till'} +
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Monterade */}
          {installed.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-green-600 uppercase mb-2 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                {t('service.tires.installed') || 'Monterade'}
              </h4>
              <div className="border rounded-lg divide-y">
                {installed.map(tire => (
                  <div key={tire.id} className="p-3 flex items-start justify-between group hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{tireTypeIcon[tire.tireType] || '🛞'}</span>
                        <span className="font-medium text-sm">{tireTypeLabels[tire.tireType] || tire.tireType}</span>
                        {tire.size && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-textSecondary">{tire.size}</span>}
                        {(tire.brand || tire.model) && (
                          <span className="text-xs text-textTertiary">{tire.brand} {tire.model}</span>
                        )}
                      </div>
                      <TreadDepthGrid tire={tire} />
                      {(tire.lastServiceDate || tire.installedDate) && (
                        <div className="text-xs text-textTertiary mt-1.5 flex items-center gap-3">
                          {tire.lastServiceDate && (
                            <span>🔧 {t('service.tires.lastChanged')}: {tire.lastServiceDate}</span>
                          )}
                          {!tire.lastServiceDate && tire.installedDate && (
                            <span>{t('service.tires.installedSince')}: {tire.installedDate}</span>
                          )}
                          {tire.installedKm != null && <span>{tire.installedKm.toLocaleString()} km</span>}
                        </div>
                      )}
                      {tire.notes && <div className="text-xs text-textTertiary italic mt-1">{tire.notes}</div>}
                    </div>
                    <button
                      onClick={() => setShowTireModal(true)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-200 rounded transition-opacity flex-shrink-0"
                      title={t('service.tires.edit') || 'Redigera'}
                    >
                      <PencilIcon className="h-4 w-4 text-textTertiary" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lagrade */}
          {stored.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-textTertiary uppercase mb-2 flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full" />
                {t('service.tires.stored') || 'Lagrade'}
              </h4>
              <div className="border rounded-lg divide-y">
                {stored.map(tire => (
                  <div key={tire.id} className="p-3 flex items-start justify-between group hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{tireTypeIcon[tire.tireType] || '🛞'}</span>
                        <span className="font-medium text-sm">{tireTypeLabels[tire.tireType] || tire.tireType}</span>
                        {tire.size && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-textSecondary">{tire.size}</span>}
                        {(tire.brand || tire.model) && (
                          <span className="text-xs text-textTertiary">{tire.brand} {tire.model}</span>
                        )}
                      </div>
                      <TreadDepthGrid tire={tire} />
                      {tire.storageLocation && (
                        <div className="text-xs text-textTertiary mt-1">📦 {t('service.tires.storage')}: {tire.storageLocation}</div>
                      )}
                      {tire.lastServiceDate && (
                        <div className="text-xs text-textTertiary mt-1">🔧 {t('service.tires.lastChanged')}: {tire.lastServiceDate}</div>
                      )}
                      {tire.notes && <div className="text-xs text-textTertiary italic mt-1">{tire.notes}</div>}
                    </div>
                    <button
                      onClick={() => setShowTireModal(true)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-200 rounded transition-opacity flex-shrink-0"
                      title={t('service.tires.edit') || 'Redigera'}
                    >
                      <PencilIcon className="h-4 w-4 text-textTertiary" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Övrigt */}
          {other.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-textTertiary uppercase mb-2">
                {t('service.tires.other') || 'Övrigt'}
              </h4>
              <div className="border rounded-lg divide-y opacity-60">
                {other.map(tire => (
                  <div key={tire.id} className="p-3 flex items-center justify-between group hover:bg-gray-50">
                    <div>
                      <span className="font-medium text-sm">{tireTypeLabels[tire.tireType] || tire.tireType}</span>
                      {(tire.brand || tire.model) && <span className="text-sm text-textTertiary ml-2">{tire.brand} {tire.model}</span>}
                      {tire.size && <span className="text-xs text-textTertiary ml-2">{tire.size}</span>}
                    </div>
                    <button
                      onClick={() => setShowTireModal(true)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-200 rounded transition-opacity"
                      title={t('service.tires.edit') || 'Redigera'}
                    >
                      <PencilIcon className="h-4 w-4 text-textTertiary" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============ Byteshistorik ============ */}
      <div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-sm font-semibold text-textSecondary hover:text-gray-800 transition-colors w-full"
        >
          <ClockIcon className="h-4 w-4" />
          {t('service.tires.changeHistory') || 'Byteshistorik'}
          {changeLog.length > 0 && (
            <span className="text-xs text-textTertiary font-normal">({changeLog.length} byten)</span>
          )}
          <span className="ml-auto">
            {showHistory
              ? <ChevronUpIcon className="h-4 w-4" />
              : <ChevronDownIcon className="h-4 w-4" />}
          </span>
        </button>

        {showHistory && (
          <div className="mt-3 space-y-2">
            {changeLog.length === 0 ? (
              <div className="text-center py-6 text-textTertiary text-sm border rounded-lg">
                <ClockIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                {t('service.tires.noHistory') || 'Inga däckbyten loggade än'}
                <div className="text-xs mt-1 text-gray-300">Historik skapas automatiskt vid nästa däckbyte</div>
              </div>
            ) : (
              changeLog.map(entry => (
                <ChangeLogRow key={entry.id} entry={entry} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Tire Management Modal */}
      {showTireModal && (
        <TireManagementModal
          vehicleId={vehicleId}
          onClose={() => setShowTireModal(false)}
          onUpdated={loadData}
        />
      )}
    </div>
  );
};

export default VehicleTiresTab;
